import { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { HeaderBar, HeaderIconButton } from '../components/HeaderBar.tsx';
import { AddAnythingSheet, type AnyEventKind } from '../components/sheets/AddAnythingSheet.tsx';
import { Icon } from '../components/Icon.tsx';
import { ButtonGrid } from '../components/home/ButtonGrid.tsx';
import { StatChip } from '../components/home/StatChip.tsx';
import { SleepStatChip } from '../components/home/SleepStatChip.tsx';
import { RecentStrip } from '../components/home/RecentStrip.tsx';
import { TimerCard } from '../components/home/TimerCard.tsx';
import { WarningBanner, type OverdueItem } from '../components/home/WarningBanner.tsx';
import { MonitorPill } from '../components/home/MonitorPill.tsx';
import { SleepSheet } from '../components/sheets/SleepSheet.tsx';
import { BottleSheet } from '../components/sheets/BottleSheet.tsx';
import { DiaperSheet } from '../components/sheets/DiaperSheet.tsx';
import { PumpStartSheet, PumpYieldSheet } from '../components/sheets/PumpSheets.tsx';
import { BreastSheet } from '../components/sheets/BreastSheet.tsx';
import { BreastPickerSheet } from '../components/sheets/BreastPickerSheet.tsx';
import { EventDetailSheet } from '../components/sheets/EventDetailSheet.tsx';
import { EventEditSheet } from '../components/sheets/EventEditSheet.tsx';
import { ALL_TILES, DEFAULT_TILES, type Tile } from '../components/home/ButtonGrid.tsx';
import type { AnyEvent } from '@shared/types.ts';
import { useMe } from '../api/hooks.ts';
import {
  useTodayEvents,
  useStartSleep,
  useStopSleep,
  useDeleteSleep,
  useReopenSleep,
  useUpdateSleep,
  useStartFeed,
  useStopFeed,
  useLogFeed,
  useDeleteFeed,
  useUpdateFeed,
  useSwitchBreast,
  usePauseFeed,
  useResumeFeed,
  useReopenFeed,
  useStartPump,
  useStopPump,
  useDeletePump,
  useUpdatePump,
  useLogDiaper,
  useDeleteDiaper,
} from '../api/events.ts';
import { ageLabel, relTime } from '../lib/format.ts';
import { computeSleepStats } from '../lib/sleep-stats.ts';
import { parseSegments, summarise, summaryLabel } from '../lib/breast.ts';
import { DEFAULTS, type TileId, type DiaperKind, type SleepLocation } from '@shared/constants.ts';
import { useNavigate } from 'react-router';

export function Home() {
  const { data: me } = useMe();
  const baby = me?.babies.find((b) => b.id === me?.active_baby_id) ?? me?.babies[0] ?? null;
  const events = useTodayEvents(baby?.id);

  const startSleep = useStartSleep();
  const stopSleep = useStopSleep();
  const delSleep = useDeleteSleep();
  const reopenSleep = useReopenSleep();
  const updateSleep = useUpdateSleep();
  const startFeed = useStartFeed();
  const stopFeed = useStopFeed();
  const logFeed = useLogFeed();
  const delFeed = useDeleteFeed();
  const updateFeed = useUpdateFeed();
  const switchBreast = useSwitchBreast();
  const pauseFeed = usePauseFeed();
  const resumeFeed = useResumeFeed();
  const reopenFeed = useReopenFeed();
  const startPump = useStartPump();
  const stopPump = useStopPump();
  const delPump = useDeletePump();
  const updatePump = useUpdatePump();
  const logDiaper = useLogDiaper();
  const delDiaper = useDeleteDiaper();

  const [collapsed, setCollapsed] = useState(false);
  const [sleepSheet, setSleepSheet] = useState<{ mode: 'start' | 'end' | 'adjust' } | null>(null);
  const [bottleSheet, setBottleSheet] = useState(false);
  const [diaperSheet, setDiaperSheet] = useState<DiaperKind | null>(null);
  const [addAnythingOpen, setAddAnythingOpen] = useState(false);
  const [pumpStartSheet, setPumpStartSheet] = useState(false);
  const [pumpYieldSheet, setPumpYieldSheet] = useState(false);
  const [breastSheet, setBreastSheet] = useState<{ side: 'left' | 'right'; mode: 'start' | 'end' | 'adjust' } | null>(null);
  const [pumpAdjustSheet, setPumpAdjustSheet] = useState(false);
  const [breastPicker, setBreastPicker] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [selectedEvent, setSelectedEvent] = useState<AnyEvent | null>(null);
  const [editingEvent, setEditingEvent] = useState<AnyEvent | null>(null);
  const nav = useNavigate();

  async function handleDeleteEvent(e: AnyEvent) {
    try {
      if (e._t === 'sleep') await delSleep.mutateAsync(e.id);
      else if (e._t === 'feed') await delFeed.mutateAsync(e.id);
      else if (e._t === 'pump') await delPump.mutateAsync(e.id);
      else await delDiaper.mutateAsync(e.id);
      toast.success('Deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  async function handleResumeEvent(e: AnyEvent) {
    try {
      if (e._t === 'sleep') await reopenSleep.mutateAsync(e.id);
      else if (e._t === 'feed') await reopenFeed.mutateAsync(e.id);
      else if (e._t === 'pump')
        await updatePump.mutateAsync({ id: e.id, patch: { ended_at: null } });
      else return;
      toast.success('Resumed');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Resume failed');
    }
  }

  // Derived tile list. The user's saved order (button_grid_config.tiles) is
  // authoritative — including which tiles are hidden. If they hide a tile in
  // the editor, we do NOT silently re-add it.
  const mergeBreast = !!me?.settings?.merge_breast;
  const savedOrder = me?.settings?.button_grid_config?.tiles ?? null;
  const tiles = useMemo<Tile[]>(() => {
    const MERGED_BREAST: Tile = {
      id: 'breast',
      label: 'Breast',
      accent: 'breast',
      icon: (p) => <Icon.breast {...p} />,
    };
    const lookup = new Map<TileId, Tile>(ALL_TILES.map((t) => [t.id, t] as const));
    lookup.set('breast', MERGED_BREAST);

    const usingSaved = !!(savedOrder && savedOrder.length);
    const baseOrder: TileId[] = usingSaved
      ? (savedOrder as TileId[])
      : DEFAULT_TILES.map((t) => t.id);

    const out: Tile[] = [];
    const seen = new Set<TileId>();
    for (const id of baseOrder) {
      // Drop the variant that conflicts with the current merge_breast setting.
      if (mergeBreast && (id === 'bL' || id === 'bR')) continue;
      if (!mergeBreast && id === 'breast') continue;
      const t = lookup.get(id);
      if (!t || seen.has(id)) continue;
      seen.add(id);
      out.push(t);
    }

    if (!usingSaved) {
      // First-run defaults — make sure breast / bL+bR appear once.
      if (mergeBreast && !seen.has('breast')) {
        const pumpIdx = out.findIndex((t) => t.id === 'pump');
        out.splice(pumpIdx === -1 ? out.length : pumpIdx, 0, MERGED_BREAST);
      } else if (!mergeBreast) {
        if (!seen.has('bL')) out.push(lookup.get('bL')!);
        if (!seen.has('bR')) out.push(lookup.get('bR')!);
      }
    } else {
      // User has a saved order. Only inject a breast tile if they toggled
      // merge_breast since saving and now have NO breast option at all —
      // otherwise their hide is final.
      if (mergeBreast && !seen.has('breast')) out.push(MERGED_BREAST);
      else if (!mergeBreast && !seen.has('bL') && !seen.has('bR')) {
        out.push(lookup.get('bL')!);
        out.push(lookup.get('bR')!);
      }
    }
    return out;
  }, [mergeBreast, savedOrder]);

  // Build overdue list from settings thresholds
  const overdueItems = useMemo((): OverdueItem[] => {
    const out: OverdueItem[] = [];
    const now = Math.floor(Date.now() / 1000);
    const sMax = (me?.settings?.sleep_max_minutes ?? DEFAULTS.sleepMaxMinutes) * 60;
    const fMax = (me?.settings?.feed_max_minutes ?? DEFAULTS.feedMaxMinutes) * 60;
    const pMax = (me?.settings?.pump_max_minutes ?? DEFAULTS.pumpMaxMinutes) * 60;
    if (events.inProgressSleep && now - events.inProgressSleep.started_at > sMax)
      out.push({
        kind: 'sleep',
        id: events.inProgressSleep.id,
        startedAt: events.inProgressSleep.started_at,
        needsReview: !!events.inProgressSleep.needs_review,
      });
    if (events.inProgressBreast && now - events.inProgressBreast.started_at > fMax)
      out.push({
        kind: 'breast',
        id: events.inProgressBreast.id,
        startedAt: events.inProgressBreast.started_at,
      });
    if (events.inProgressPump && now - events.inProgressPump.started_at > pMax)
      out.push({
        kind: 'pump',
        id: events.inProgressPump.id,
        startedAt: events.inProgressPump.started_at,
        needsReview: !!events.inProgressPump.needs_review,
      });
    return out;
  }, [
    events.inProgressSleep,
    events.inProgressBreast,
    events.inProgressPump,
    me?.settings?.sleep_max_minutes,
    me?.settings?.feed_max_minutes,
    me?.settings?.pump_max_minutes,
  ]);

  // Current activity state for tile highlighting
  const activeIds = new Set<TileId>();
  if (events.inProgressSleep) activeIds.add('sleep');
  if (events.inProgressPump) activeIds.add('pump');
  if (events.inProgressBreast?.type === 'breast_left') activeIds.add('bL');
  if (events.inProgressBreast?.type === 'breast_right') activeIds.add('bR');
  if (events.inProgressBreast) activeIds.add('breast');

  // Hints derived from last events. Tick once a minute so "Xm ago" / "Xh ago"
  // stays current without waiting for the next event refetch.
  const [agoTick, setAgoTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setAgoTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hints = useMemo(() => computeHints(events), [events, agoTick]);

  // Today rollups
  const todayStart = startOfTodaySec();
  const sleepStats = useMemo(() => computeSleepStats(events.sleep), [events.sleep]);
  // Live "awake since" counter — minutes since the END of the most recent
  // completed sleep. Null while a sleep is in progress (the chip's pulse
  // already conveys "sleeping right now"). Re-derives every minute via the
  // existing agoTick.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const awakeSinceMins = useMemo<number | null>(() => {
    if (events.inProgressSleep) return null;
    const lastEnded = events.sleep.find((s) => s.ended_at != null)?.ended_at;
    if (!lastEnded) return null;
    return Math.max(0, Math.floor((Date.now() / 1000 - lastEnded) / 60));
  }, [events.sleep, events.inProgressSleep, agoTick]);
  const feedsToday = events.feed.filter((f) => f.started_at >= todayStart).length;
  const diapersToday = events.diaper.filter((d) => d.occurred_at >= todayStart).length;

  // Primary tap handlers
  async function onPrimary(id: TileId): Promise<void> {
    if (!baby) return;
    // The opt-in tiles are pure navigations — same behaviour as picking them
    // from the "Log anything" sheet on the Home header.
    if (id === 'measurement' || id === 'milestone' || id === 'medication' || id === 'temperature') {
      return onAddAnything(id);
    }
    try {
      if (id === 'sleep') {
        if (events.inProgressSleep) {
          const stoppedId = events.inProgressSleep.id;
          await stopSleep.mutateAsync();
          toast.success('Sleep ended', {
            duration: 8000,
            action: {
              label: 'Undo',
              // Re-open the session, don't delete it.
              onClick: () => reopenSleep.mutateAsync(stoppedId),
            },
          });
        } else {
          const lastLoc = lastSleepLocation(events.sleep);
          const r = await startSleep.mutateAsync({ location: lastLoc ?? 'cot' });
          toast.success(`Sleep started · ${r.session.location ?? 'cot'}`, {
            duration: 8000,
            action: {
              label: 'Undo',
              onClick: () => {
                if (r.session.id) delSleep.mutateAsync(r.session.id);
              },
            },
          });
        }
        return;
      }

      if (id === 'bottle') {
        // Two-tap: open the sheet, never auto-log
        setBottleSheet(true);
        return;
      }
      if (id === 'solid') {
        // Two-tap: solid has no auto-log (no last-amount to assume)
        const r = await logFeed.mutateAsync({ type: 'solid' });
        toast.success('Solid logged', {
          duration: 8000,
          action: { label: 'Undo', onClick: () => delFeed.mutateAsync(r.session.id) },
        });
        return;
      }

      if (id === 'bL' || id === 'bR') {
        const type = id === 'bL' ? 'breast_left' : 'breast_right';
        const inProg = events.feed.find((f) => f.type === type && f.ended_at == null);
        if (inProg) {
          const stoppedId = inProg.id;
          await stopFeed.mutateAsync({ type });
          toast.success(`Breast ${id === 'bL' ? 'L' : 'R'} ended`, {
            duration: 8000,
            action: { label: 'Undo', onClick: () => reopenFeed.mutateAsync(stoppedId) },
          });
        } else {
          const r = await startFeed.mutateAsync({ type });
          toast.success(`Breast ${id === 'bL' ? 'L' : 'R'} started`, {
            duration: 8000,
            action: { label: 'Undo', onClick: () => delFeed.mutateAsync(r.session.id) },
          });
        }
        return;
      }

      if (id === 'pump') {
        if (events.inProgressPump) {
          setPumpYieldSheet(true);
        } else {
          const lastSide = events.pump[0]?.side ?? 'both';
          const r = await startPump.mutateAsync({ side: lastSide ?? 'both' });
          toast.success(`Pump started · ${r.session.side ?? 'both'}`, {
            duration: 6000,
            action: { label: 'Undo', onClick: () => delPump.mutateAsync(r.session.id) },
          });
        }
        return;
      }

      if (id === 'diaper') {
        // Two-tap: open the sheet so the user picks pee / poop / both etc.
        setDiaperSheet('pee');
        return;
      }

      if (id === 'breast') {
        // Merged tile: stop the running feed, or pop the L/R picker.
        if (events.inProgressBreast) {
          const inProg = events.inProgressBreast;
          const type = inProg.type;
          const stoppedId = inProg.id;
          await stopFeed.mutateAsync({ type });
          toast.success('Breast feed ended', {
            duration: 8000,
            action: { label: 'Undo', onClick: () => reopenFeed.mutateAsync(stoppedId) },
          });
        } else {
          setBreastPicker(true);
        }
        return;
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed');
    }
  }

  function onAddAnything(kind: AnyEventKind): void {
    switch (kind) {
      case 'sleep':
        setSleepSheet({ mode: events.inProgressSleep ? 'adjust' : 'start' });
        return;
      case 'breast':
        if (events.inProgressBreast) {
          setBreastSheet({
            side: events.inProgressBreast.type === 'breast_left' ? 'left' : 'right',
            mode: 'adjust',
          });
        } else {
          setBreastPicker(true);
        }
        return;
      case 'bottle':
        setBottleSheet(true);
        return;
      case 'solid':
        void onPrimary('solid');
        return;
      case 'pump':
        if (events.inProgressPump) setPumpYieldSheet(true);
        else setPumpStartSheet(true);
        return;
      case 'diaper':
        setDiaperSheet('pee');
        return;
      case 'measurement':
        nav('/growth?add=1');
        return;
      case 'milestone':
        nav('/milestones?add=1');
        return;
      case 'medication':
        nav('/health?add=medication');
        return;
      case 'temperature':
        nav('/health?add=temperature');
        return;
    }
  }

  function onMore(id: TileId) {
    if (id === 'sleep') return setSleepSheet({ mode: events.inProgressSleep ? 'end' : 'start' });
    if (id === 'bottle') return setBottleSheet(true);
    if (id === 'pump')
      return events.inProgressPump ? setPumpYieldSheet(true) : setPumpStartSheet(true);
    if (id === 'diaper') return setDiaperSheet('pee');
    if (id === 'bL') return setBreastSheet({ side: 'left', mode: events.inProgressBreast?.type === 'breast_left' ? 'end' : 'start' });
    if (id === 'bR') return setBreastSheet({ side: 'right', mode: events.inProgressBreast?.type === 'breast_right' ? 'end' : 'start' });
    if (id === 'breast') {
      if (events.inProgressBreast) {
        return setBreastSheet({
          side: events.inProgressBreast.type === 'breast_left' ? 'left' : 'right',
          mode: 'adjust',
        });
      }
      return setBreastPicker(true);
    }
    if (id === 'solid') return onPrimary('solid');
    if (id === 'measurement' || id === 'milestone' || id === 'medication' || id === 'temperature') {
      return onAddAnything(id);
    }
  }

  return (
    <>
      <HeaderBar
        baby={baby}
        title={baby?.name ?? 'Lion Tracker'}
        subtitle={baby ? ageLabel(baby.birth_date) : null}
        rightSlot={
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => nav('/events')}
              style={{
                width: 38,
                height: 38,
                borderRadius: 999,
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                color: 'var(--text-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label="All events"
            >
              <Icon.bell s={18} />
            </button>
            <HeaderIconButton ariaLabel="Log anything" onClick={() => setAddAnythingOpen(true)}>
              <Icon.plus s={18} />
            </HeaderIconButton>
          </div>
        }
      />
      <div className="lt-scroll" style={{ flex: 1, paddingBottom: 20 }}>
        <WarningBanner
          items={overdueItems}
          dismissed={dismissed}
          onDismiss={(id) => setDismissed((d) => new Set([...d, id]))}
          onEnd={(it) => {
            if (it.kind === 'sleep') onPrimary('sleep');
            else if (it.kind === 'pump') setPumpYieldSheet(true);
            else
              setBreastSheet({
                side: events.inProgressBreast?.type === 'breast_left' ? 'left' : 'right',
                mode: 'end',
              });
          }}
          onAdjust={(it) => {
            if (it.kind === 'sleep') setSleepSheet({ mode: 'adjust' });
            else if (it.kind === 'pump') setPumpYieldSheet(true);
            else
              setBreastSheet({
                side: events.inProgressBreast?.type === 'breast_left' ? 'left' : 'right',
                mode: 'end',
              });
          }}
        />
        <MonitorPill />
        {events.inProgressSleep && (
          <TimerCard
            kind="sleep"
            label={`Sleep · ${events.inProgressSleep.location ?? 'cot'}`}
            startedAt={events.inProgressSleep.started_at}
            by={byOf(me?.members ?? [], events.inProgressSleep.created_by)}
            onEnd={() => onPrimary('sleep')}
            onAdjust={() => setSleepSheet({ mode: 'adjust' })}
          />
        )}
        {events.inProgressBreast && (() => {
          const f = events.inProgressBreast;
          // When paused, the last segment.to is the pause start.
          const segs = parseSegments(f.segments);
          const pausedSince = f.is_paused && segs.length > 0 ? segs[segs.length - 1].to : undefined;
          return (
            <TimerCard
              kind="breast"
              label={`Breast ${f.type === 'breast_left' ? 'L' : 'R'}`}
              startedAt={f.started_at}
              by={byOf(me?.members ?? [], f.created_by)}
              isPaused={!!f.is_paused}
              pausedSince={pausedSince}
              getDetail={(nowSec) => {
                const s = summarise(f, nowSec);
                return s.hasHistory ? summaryLabel(s, { showPaused: true }) : undefined;
              }}
              onEnd={() => onPrimary(f.type === 'breast_left' ? 'bL' : 'bR')}
              onAdjust={() =>
                setBreastSheet({
                  side: f.type === 'breast_left' ? 'left' : 'right',
                  mode: 'adjust',
                })
              }
              switchLabel={`Switch to ${f.type === 'breast_left' ? 'R' : 'L'}`}
              onSwitch={async () => {
                try {
                  const r = await switchBreast.mutateAsync(f.id);
                  toast.success(
                    `Switched to breast ${r.session.type === 'breast_left' ? 'L' : 'R'}`,
                    {
                      duration: 6000,
                      action: {
                        label: 'Undo',
                        onClick: () => switchBreast.mutateAsync(f.id),
                      },
                    },
                  );
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : 'Failed');
                }
              }}
              onPauseToggle={async () => {
                try {
                  if (f.is_paused) {
                    await resumeFeed.mutateAsync(f.id);
                    toast.success('Resumed');
                  } else {
                    await pauseFeed.mutateAsync(f.id);
                    toast.success('Paused');
                  }
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : 'Failed');
                }
              }}
            />
          );
        })()}
        {events.inProgressPump && (
          <TimerCard
            kind="pump"
            label={`Pump · ${events.inProgressPump.side ?? 'both'}`}
            startedAt={events.inProgressPump.started_at}
            by={byOf(me?.members ?? [], events.inProgressPump.created_by)}
            onEnd={() => onPrimary('pump')}
            onAdjust={() => setPumpAdjustSheet(true)}
          />
        )}

        <div style={{ padding: '18px 16px 6px', display: 'flex', gap: 8, alignItems: 'stretch' }}>
          <SleepStatChip
            lastNightMins={sleepStats.lastNightMins}
            napsTodayMins={sleepStats.napsTodayMins}
            tonightSoFarMins={sleepStats.tonightSoFarMins}
            awakeSinceMins={awakeSinceMins}
          />
          <StatChip label="Feeds" value={String(feedsToday)} />
          <StatChip label="Diapers" value={String(diapersToday)} />
        </div>

        <div style={{ paddingTop: 10 }}>
          <ButtonGrid
            tiles={tiles}
            hints={hints}
            activeIds={activeIds}
            collapsed={collapsed}
            onToggle={() => setCollapsed((c) => !c)}
            onPrimary={onPrimary}
            onMore={onMore}
            density={(me?.settings?.density ?? '3col') as '2col' | '3col'}
          />
        </div>

        <div style={{ height: 18 }} />
        <RecentStrip
          items={events.combined}
          usersById={byMap(me?.members ?? [])}
          onSelect={(e) => setSelectedEvent(e)}
        />
      </div>

      {sleepSheet && (
        <SleepSheet
          open
          mode={sleepSheet.mode}
          session={events.inProgressSleep}
          defaultLocation={(lastSleepLocation(events.sleep) as SleepLocation | null) ?? 'cot'}
          onClose={() => setSleepSheet(null)}
          onSubmit={async ({ location, notes, started_at }) => {
            try {
              if (sleepSheet.mode === 'start') {
                const r = await startSleep.mutateAsync({ location });
                toast.success(`Sleep started · ${r.session.location ?? 'cot'}`, {
                  duration: 6000,
                  action: { label: 'Undo', onClick: () => delSleep.mutateAsync(r.session.id) },
                });
              } else if (sleepSheet.mode === 'adjust') {
                if (!events.inProgressSleep) return;
                const patch: Record<string, unknown> = {
                  location,
                  notes: notes ?? null,
                };
                if (started_at != null) patch.started_at = started_at;
                await updateSleep.mutateAsync({
                  id: events.inProgressSleep.id,
                  patch,
                });
                toast.success('Sleep updated');
              } else {
                // 'end' — apply location/notes/started_at changes (if any) then stop.
                if (events.inProgressSleep) {
                  const cur = events.inProgressSleep;
                  const patch: Record<string, unknown> = {};
                  if (cur.location !== location) patch.location = location;
                  if ((cur.notes ?? '') !== (notes ?? '')) patch.notes = notes ?? null;
                  if (started_at != null) patch.started_at = started_at;
                  if (Object.keys(patch).length) {
                    await updateSleep.mutateAsync({ id: cur.id, patch });
                  }
                }
                const stoppedId = events.inProgressSleep?.id;
                await stopSleep.mutateAsync();
                toast.success('Sleep ended', {
                  duration: 8000,
                  action: stoppedId
                    ? { label: 'Undo', onClick: () => reopenSleep.mutateAsync(stoppedId) }
                    : undefined,
                });
              }
            } catch (e) {
              toast.error(e instanceof Error ? e.message : 'Failed');
            }
          }}
        />
      )}

      <AddAnythingSheet
        open={addAnythingOpen}
        onClose={() => setAddAnythingOpen(false)}
        onPick={onAddAnything}
      />

      <BottleSheet
        open={bottleSheet}
        onClose={() => setBottleSheet(false)}
        defaultAmount={events.feed.find((f) => f.type === 'bottle')?.amount_ml ?? 140}
        onSubmit={async ({ amount_ml, content }) => {
          try {
            const r = await logFeed.mutateAsync({ type: 'bottle', amount_ml, content });
            toast.success(`Bottle · ${amount_ml} ml`, {
              duration: 6000,
              action: { label: 'Undo', onClick: () => delFeed.mutateAsync(r.session.id) },
            });
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed');
          }
        }}
      />

      <DiaperSheet
        open={!!diaperSheet}
        defaultKind={diaperSheet ?? 'pee'}
        onClose={() => setDiaperSheet(null)}
        onSubmit={async (vars) => {
          try {
            const r = await logDiaper.mutateAsync(vars);
            toast.success(`Diaper · ${vars.kind}`, {
              duration: 6000,
              action: { label: 'Undo', onClick: () => delDiaper.mutateAsync(r.event.id) },
            });
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed');
          }
        }}
      />

      <PumpStartSheet
        open={pumpStartSheet}
        onClose={() => setPumpStartSheet(false)}
        defaultSide={events.pump[0]?.side ?? 'both'}
        onSubmit={async ({ side }) => {
          try {
            const r = await startPump.mutateAsync({ side });
            toast.success(`Pump started · ${side}`, {
              duration: 6000,
              action: { label: 'Undo', onClick: () => delPump.mutateAsync(r.session.id) },
            });
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed');
          }
        }}
      />

      <PumpStartSheet
        open={pumpAdjustSheet && !!events.inProgressPump}
        onClose={() => setPumpAdjustSheet(false)}
        mode="adjust"
        defaultSide={(events.inProgressPump?.side ?? 'both') as 'left' | 'right' | 'both'}
        onSubmit={async ({ side }) => {
          if (!events.inProgressPump) return;
          try {
            await updatePump.mutateAsync({
              id: events.inProgressPump.id,
              patch: { side },
            });
            toast.success('Pump updated');
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed');
          }
        }}
      />

      <PumpYieldSheet
        open={pumpYieldSheet}
        onClose={() => setPumpYieldSheet(false)}
        defaultLeft={0}
        defaultRight={0}
        side={events.inProgressPump?.side ?? 'both'}
        onSubmit={async ({ yield_left_ml, yield_right_ml, yield_ml }) => {
          try {
            await stopPump.mutateAsync({
              id: events.inProgressPump?.id,
              yield_left_ml,
              yield_right_ml,
              yield_ml,
            });
            toast.success(`Pump · ${yield_ml} ml`);
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed');
          }
        }}
        onSkip={async () => {
          try {
            await stopPump.mutateAsync({ id: events.inProgressPump?.id });
            toast.success('Pump ended');
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed');
          }
        }}
      />

      {breastSheet && (
        <BreastSheet
          open
          side={breastSheet.side}
          mode={breastSheet.mode}
          session={events.inProgressBreast}
          onClose={() => setBreastSheet(null)}
          onSubmit={async ({ notes, started_at }) => {
            try {
              const type = breastSheet.side === 'left' ? 'breast_left' : 'breast_right';
              const label = `Breast ${breastSheet.side[0]?.toUpperCase()}`;
              if (breastSheet.mode === 'start') {
                await startFeed.mutateAsync({ type });
                toast.success(`${label} started`);
              } else if (breastSheet.mode === 'adjust') {
                if (!events.inProgressBreast) return;
                const patch: Record<string, unknown> = { notes: notes ?? null };
                if (started_at != null) patch.started_at = started_at;
                await updateFeed.mutateAsync({
                  id: events.inProgressBreast.id,
                  patch,
                });
                toast.success(`${label} updated`);
              } else {
                if (events.inProgressBreast) {
                  const patch: Record<string, unknown> = {};
                  if ((events.inProgressBreast.notes ?? '') !== (notes ?? '')) patch.notes = notes ?? null;
                  if (started_at != null) patch.started_at = started_at;
                  if (Object.keys(patch).length) {
                    await updateFeed.mutateAsync({
                      id: events.inProgressBreast.id,
                      patch,
                    });
                  }
                }
                const stoppedId = events.inProgressBreast?.id;
                await stopFeed.mutateAsync({ type });
                toast.success(`${label} ended`, {
                  duration: 8000,
                  action: stoppedId
                    ? { label: 'Undo', onClick: () => reopenFeed.mutateAsync(stoppedId) }
                    : undefined,
                });
              }
            } catch (e) {
              toast.error(e instanceof Error ? e.message : 'Failed');
            }
          }}
        />
      )}

      <BreastPickerSheet
        open={breastPicker}
        onClose={() => setBreastPicker(false)}
        lastBreast={
          events.feed.find(
            (f) => (f.type === 'breast_left' || f.type === 'breast_right') && f.ended_at != null,
          ) ?? null
        }
        onPick={async (side) => {
          try {
            const type = side === 'left' ? 'breast_left' : 'breast_right';
            const r = await startFeed.mutateAsync({ type });
            toast.success(`Breast ${side === 'left' ? 'L' : 'R'} started`, {
              duration: 6000,
              action: { label: 'Undo', onClick: () => delFeed.mutateAsync(r.session.id) },
            });
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed');
          }
        }}
      />

      <EventDetailSheet
        open={!!selectedEvent}
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onDelete={handleDeleteEvent}
        onResume={handleResumeEvent}
        onEdit={(e) => {
          setSelectedEvent(null);
          setEditingEvent(e);
        }}
      />
      <EventEditSheet
        open={!!editingEvent}
        event={editingEvent}
        onClose={() => setEditingEvent(null)}
      />
    </>
  );
}

function startOfTodaySec(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
}

function lastSleepLocation(sleeps: { location: string | null }[]): SleepLocation | null {
  for (const s of sleeps) if (s.location) return s.location as SleepLocation;
  return null;
}

function byOf<T extends { id: string }>(members: T[], id: string): T | undefined {
  return members.find((m) => m.id === id);
}

function byMap<T extends { id: string }>(members: T[]): Record<string, T> {
  return Object.fromEntries(members.map((m) => [m.id, m]));
}

function computeHints(events: ReturnType<typeof useTodayEvents>): Partial<Record<TileId, string>> {
  // Every hint follows `<last state> · <ago>` so the user can scan time-since
  // at a glance. `relTime` rounds (just now / Xm ago / Xh ago / Xd ago).
  const out: Partial<Record<TileId, string>> = {};
  const join = (state: string | null, sec: number): string =>
    state ? `${state} · ${relTime(sec)}` : relTime(sec);

  const lastSleep = events.sleep.find((s) => s.ended_at != null);
  if (lastSleep) out.sleep = join(lastSleep.location ?? null, lastSleep.ended_at!);

  const lastBottle = events.feed.find((f) => f.type === 'bottle');
  if (lastBottle) out.bottle = join(lastBottle.amount_ml ? `${lastBottle.amount_ml} ml` : null, lastBottle.started_at);

  // Most recent finished breast feed — show the *session* summary on both
  // L and R tiles, since one logical feed can span both sides via Switch.
  const lastBreast = events.feed.find(
    (f) => (f.type === 'breast_left' || f.type === 'breast_right') && f.ended_at != null,
  );
  if (lastBreast) {
    const total = Math.max(1, Math.round((lastBreast.ended_at! - lastBreast.started_at) / 60));
    const s = summarise(lastBreast);
    const sideSummary = s.hasHistory ? summaryLabel(s) : '';
    const state = sideSummary || `${total}m`;
    const hint = join(state, lastBreast.ended_at!);
    out.bL = hint;
    out.bR = hint;
    out.breast = hint;
  }

  const lastPump = events.pump.find((p) => p.ended_at);
  if (lastPump) out.pump = join(lastPump.yield_ml ? `${lastPump.yield_ml} ml` : null, lastPump.ended_at!);

  const lastSolid = events.feed.find((f) => f.type === 'solid');
  if (lastSolid) out.solid = join(null, lastSolid.started_at);

  const lastDiaper = events.diaper[0];
  if (lastDiaper) out.diaper = join(lastDiaper.kind, lastDiaper.occurred_at);

  return out;
}
